/**
 * Generic GraphQL mock resolver for the prototype shell.
 *
 * Instead of hand-writing a handler per operation (50+), we walk the actual query AST
 * and synthesize a correctly-shaped response from GQty's generated schema. Any query the
 * app fires gets populated, faker-generated data back — no backend, no SDL, no new deps
 * (graphql.parse + faker are already installed). Domain semantics are fake; shapes are real.
 *
 * DETERMINISM: every scalar is generated from a faker seed derived from its *path*
 * (e.g. "query.sites[0].siteId"), not from a running RNG. So the same logical field
 * returns the same value across every fetch and every component — which is what makes
 * stateful flows work (select a location → navigate to it → it still exists). Random,
 * call-order-dependent data would break those flows.
 *
 * Single entry point: mockResolve(query, variables) -> the unwrapped `data` object
 * (matching what graphql-request's client.request() resolves to).
 */
import { parse } from 'graphql'
import { SchemaUnionsKey } from 'gqty'
import { faker } from '@faker-js/faker'
import * as SchemaModule from '../gqty/schema.generated'
import { ALL_ACCESS_PERMISSIONS } from './config'
import { CURATED } from './fixtures'

// Resolve a GraphQL AST argument value (incl. $variables and nested input objects) to JS.
function valueFromNode(node, variables) {
  switch (node?.kind) {
    case 'Variable':
      return variables?.[node.name.value]
    case 'IntValue':
      return parseInt(node.value, 10)
    case 'FloatValue':
      return parseFloat(node.value)
    case 'StringValue':
    case 'EnumValue':
      return node.value
    case 'BooleanValue':
      return node.value
    case 'NullValue':
      return null
    case 'ListValue':
      return node.values.map((v) => valueFromNode(v, variables))
    case 'ObjectValue': {
      const o = {}
      for (const f of node.fields) o[f.name.value] = valueFromNode(f.value, variables)
      return o
    }
    default:
      return undefined
  }
}
function argsToObject(argNodes, variables) {
  const o = {}
  for (const a of argNodes || []) o[a.name.value] = valueFromNode(a.value, variables)
  return o
}

const { generatedSchema, scalarsEnumsHash } = SchemaModule
const UNIONS = generatedSchema[SchemaUnionsKey] || {}

const BROWSER_TZ =
  (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC'

// Fixed "now" captured once at module load so epoch-ms fields are stable within a session
// (deterministic → GQty caches them; non-deterministic times cause re-fetch loops).
const BASE_MS = Date.now()

const BUILTIN_SCALARS = new Set([
  'ID',
  'String',
  'Boolean',
  'Int',
  'Float',
  'JSON',
  'JSONObject',
  'Numeric',
])

// Harvest enum value lists from the schema module's exported TS enums (compile to objects).
const ENUM_VALUES = {}
for (const [name, value] of Object.entries(SchemaModule)) {
  if (scalarsEnumsHash[name] && value && typeof value === 'object' && !Array.isArray(value)) {
    const vals = Object.values(value).filter((v) => typeof v === 'string')
    if (vals.length) ENUM_VALUES[name] = vals
  }
}

// djb2 — small deterministic string hash → 32-bit unsigned int for faker.seed().
function hashString(str) {
  let h = 5381
  for (let i = 0; i < str.length; i++) h = (h * 33) ^ str.charCodeAt(i)
  return h >>> 0
}

const isLeaf = (typeName) => BUILTIN_SCALARS.has(typeName) || scalarsEnumsHash[typeName] === true

// 'String!' -> {name:'String'}; '[Site!]!' -> {name:'Site', isList:true}
const unwrapType = (typeRef) => {
  let t = String(typeRef).replace(/!/g, '')
  let isList = false
  if (t.startsWith('[')) {
    isList = true
    t = t.slice(1, -1)
  }
  return { name: t, isList }
}

const has = (field, ...needles) => needles.some((n) => field.includes(n))

const isoDate = () => faker.date.recent({ days: 60 }).toISOString()

function stringValue(field) {
  const f = field.toLowerCase()
  if (field === 'staffUserPermissions' || f === 'permissions') return ALL_ACCESS_PERMISSIONS
  if (has(f, 'email')) return faker.internet.email()
  if (f.includes('firstname')) return faker.person.firstName()
  if (f.includes('lastname')) return faker.person.lastName()
  if (f.includes('username')) return faker.internet.username()
  if (has(f, 'phone', 'mobile')) return faker.phone.number()
  if (has(f, 'avatar', 'image', 'photo', 'logo', 'picture', 'thumbnail'))
    return `https://picsum.photos/seed/${faker.string.alphanumeric(6)}/200/200`
  if (has(f, 'url', 'link', 'website')) return faker.internet.url()
  // Money/amount fields are sometimes String-typed (e.g. taxAmount) — return a numeric string
  // so the component's Number()/formatting yields a real value, not "$ NaN".
  if (has(f, 'amount', 'price', 'subtotal', 'balance', 'cost'))
    return String(faker.number.float({ min: 1, max: 500, fractionDigits: 2 }))
  if (f === 'rate' || f === 'taxrate' || has(f, 'percent'))
    return String(faker.number.int({ min: 5, max: 18 }))
  // Timezone must be a valid IANA zone (date-fns-tz throws on garbage). Check before the
  // date heuristic, since "timezone" contains the substring "time". Use the BROWSER's zone so
  // generated session times (built with local setHours) render at the intended hour on the
  // schedule (the calendar renders events in the site's timezone field).
  if (has(f, 'timezone') || f === 'tz') return BROWSER_TZ
  // Date-only fields → 'yyyy-MM-dd'. Many components do `new Date(field + 'T12:00:00')` or
  // otherwise assume a plain date; a full ISO string breaks that ("Invalid time value").
  if (
    (f.includes('date') && !f.includes('datetime')) ||
    f === 'dob' ||
    has(f, 'birthday', 'expiry', 'expire')
  )
    return isoDate().slice(0, 10)
  // Full timestamp fields → ISO. (Non-date here crashes date-fns.)
  if (
    f.endsWith('at') ||
    f.endsWith('time') ||
    has(f, 'time', 'timestamp', 'datetime', 'schedule', 'birth') ||
    ['start', 'end', 'since', 'until', 'from', 'dt'].includes(f)
  )
    return isoDate()
  if (f.includes('status')) return 'active'
  if (has(f, 'color', 'colour')) return faker.internet.color()
  if (f.includes('currency')) return 'USD'
  if (f.includes('country')) return faker.location.country()
  if (f.includes('city')) return faker.location.city()
  if (has(f, 'state', 'province')) return faker.location.state()
  if (has(f, 'zip', 'postal')) return faker.location.zipCode()
  if (has(f, 'address', 'street')) return faker.location.streetAddress()
  if (has(f, 'description', 'note', 'message', 'body', 'content', 'bio', 'summary'))
    return faker.lorem.sentence()
  if (has(f, 'sitename', 'brandname', 'companyname', 'businessname', 'organisation'))
    return faker.company.name()
  if (f.endsWith('name') || has(f, 'title', 'label')) return faker.commerce.productName()
  if (f.endsWith('id') || f === 'id') return faker.string.uuid()
  if (f.includes('slug')) return faker.lorem.slug()
  if (f.includes('code')) return faker.string.alphanumeric(6).toUpperCase()
  return faker.lorem.word()
}

function numberValue(field, typeName) {
  const f = field.toLowerCase()
  // Whole-number things win even if the name also contains "total" (e.g. totalActiveMembers).
  const countish = has(
    f,
    'member',
    'visit',
    'count',
    'attendance',
    'booking',
    'class',
    'session',
    'quantity',
    'qty',
    'age',
    'capacity',
    'credit',
    'rank',
    'order',
    'day',
    'week',
    'month',
    'year',
    'people',
    'staff',
    'client',
    'lead',
    'attempt',
    'retry',
    'duration',
    'position',
    'priority',
    'version'
  )
  const money = has(
    f,
    'price',
    'amount',
    'cost',
    'fee',
    'balance',
    'revenue',
    'value',
    'salary',
    'payment',
    'total',
    'subtotal'
  )
  if (f.includes('lat')) return Number(faker.location.latitude())
  if (has(f, 'lng', 'lon')) return Number(faker.location.longitude())
  // tax/discount rates & percentages stay realistic (numeric fields only, so 'rate' is safe).
  if (has(f, 'percent') || f === 'rate' || f.endsWith('rate'))
    return faker.number.int({ min: 5, max: 18 })
  if (countish) return faker.number.int({ min: 1, max: 500 })
  if (typeName === 'Float' || typeName === 'Numeric' || money)
    return faker.number.float({ min: 1, max: 1000, fractionDigits: 2 })
  return faker.number.int({ min: 1, max: 50 })
}

function booleanValue(field) {
  const f = field.toLowerCase()
  if (has(f, 'hasnext', 'hasprevious', 'hasmore')) return false // stop pagination loops
  if (
    has(f, 'deleted', 'disabled', 'archived', 'hidden', 'blocked', 'cancelled', 'expired', 'banned')
  )
    return false
  // "Already-has / already-claimed" gates default false, else sale/checkout flows get blocked
  // (e.g. New Sale: isIntroOffer + hasServiceAtSite → "intro offer already claimed").
  if (has(f, 'introoffer', 'isintro', 'hasservice', 'claimed', 'duplicate', 'alreadyhas'))
    return false
  return true // lean true: capabilities/flags on → more UI visible
}

// Fields whose exact FORMAT a consuming library cares about (FullCalendar etc.), where a
// generic faker value would crash. Keep this small — only add when a real crash demands it.
const FIELD_OVERRIDES = {
  calendarMinTime: '06:00', // FullCalendar slotMinTime, code appends ":00" → "06:00:00"
  calendarMaxTime: '21:00',
  calendarInterval: 30, // minutes → "00:30:00"
  calendarStartDay: 'monday',
}

// Generate one scalar, deterministically seeded by its path so it's stable across fetches.
function scalarValue(field, typeName, seedKey) {
  if (Object.prototype.hasOwnProperty.call(FIELD_OVERRIDES, field)) return FIELD_OVERRIDES[field]
  // `taxSetting.amount` is a tax RATE (percentage), not a money amount — without this the
  // payment-detail page sums several of them into "2993% excl". Targeted via the seed path.
  if (field === 'amount' && seedKey.toLowerCase().includes('taxsetting'))
    return String(5 + (hashString(seedKey) % 11))
  // start/end time fields have two shapes in this app:
  //  - "HH:MM" clock strings for recurrence splits & business/working/day hours
  //    (components do `time.split(':')`)
  //  - epoch milliseconds for sessions (components do `Number(startTime)` → new Date(ms))
  // ISO here breaks both (split → "Invalid time value"; session → NaN → form never prefills).
  if (field === 'startTime' || field === 'endTime') {
    const p = seedKey.toLowerCase()
    if (p.includes('split') || p.includes('hours'))
      return `${String(6 + (hashString(seedKey) % 13)).padStart(2, '0')}:00`
    return String(
      BASE_MS + (((hashString(seedKey) % 14) - 7) * 86400000 + (hashString(field) % 12) * 3600000)
    )
  }
  faker.seed(hashString(seedKey + ':' + field + ':' + typeName))
  if (ENUM_VALUES[typeName]) {
    if (field === 'staffUserPermissions') return ALL_ACCESS_PERMISSIONS
    const vals = ENUM_VALUES[typeName]
    // Status-ish enums: prefer a positive value over the alphabetically-first (often
    // "Cancelled"/"Failed"), so receipts/chips read sensibly instead of "Cancelled".
    if (field.toLowerCase().includes('status')) {
      const good = vals.find((v) =>
        /^(active|paid|completed?|confirmed?|success|succeeded|scheduled|approved|open|live|enabled|processing|sent)$/i.test(
          v
        )
      )
      if (good) return good
    }
    return vals[0]
  }
  switch (typeName) {
    case 'ID':
      return faker.string.uuid()
    case 'Boolean':
      return booleanValue(field)
    case 'Int':
    case 'Float':
    case 'Numeric':
      return numberValue(field, typeName)
    case 'JSON':
    case 'JSONObject':
      return {}
    default:
      return stringValue(field)
  }
}

const listCount = (field) =>
  has(field.toLowerCase(), 'results', 'edges', 'nodes', 'items', 'data', 'rows', 'records') ? 6 : 4

function resolveField(field, typeName, isList, selectionSet, fragments, depth, seedKey) {
  const makeOne = (key) => {
    if (isLeaf(typeName)) return scalarValue(field, typeName, key)
    if (!selectionSet) return null
    return resolveSelectionSet(typeName, selectionSet, fragments, depth + 1, key)
  }
  if (isList) {
    // A list of an enum → return EVERY enum value (e.g. "which item types / features are
    // enabled"). N copies of the first value are useless and can hide UI — that's what made
    // New Sale show only Gift Cards instead of all of packages/memberships/products/etc.
    if (ENUM_VALUES[typeName]) return [...ENUM_VALUES[typeName]]
    return Array.from({ length: listCount(field) }, (_, i) => makeOne(`${seedKey}.${field}[${i}]`))
  }
  return makeOne(`${seedKey}.${field}`)
}

function resolveSelectionSet(typeName, selectionSet, fragments, depth, seedKey, variables) {
  if (depth > 12) return null // safety against pathological fragment nesting
  // Unions: no own fields — concretize to the first member for __typename.
  const concreteType = UNIONS[typeName] ? UNIONS[typeName][0] : typeName
  const typeFields = generatedSchema[concreteType]
  const obj = { __typename: concreteType }

  for (const sel of selectionSet.selections) {
    if (sel.kind === 'Field') {
      const name = sel.name.value
      const alias = sel.alias?.value || name
      if (name === '__typename') {
        obj[alias] = concreteType
        continue
      }
      // Curated fixtures apply only at the root (variables is passed only there).
      if (variables && CURATED[name]) {
        obj[alias] = CURATED[name](argsToObject(sel.arguments, variables))
        continue
      }
      const def = typeFields?.[name]
      if (!def) {
        obj[alias] = null
        continue
      }
      const { name: retType, isList } = unwrapType(def.__type)
      obj[alias] = resolveField(name, retType, isList, sel.selectionSet, fragments, depth, seedKey)
    } else if (sel.kind === 'InlineFragment') {
      const onType = sel.typeCondition?.name.value
      if (onType && (!UNIONS[typeName] || onType === concreteType)) {
        Object.assign(obj, resolveSelectionSet(onType, sel.selectionSet, fragments, depth, seedKey))
      }
    } else if (sel.kind === 'FragmentSpread') {
      const frag = fragments[sel.name.value]
      if (frag) {
        Object.assign(
          obj,
          resolveSelectionSet(
            frag.typeCondition.name.value,
            frag.selectionSet,
            fragments,
            depth,
            seedKey
          )
        )
      }
    }
  }
  return obj
}

// parse() is not cheap on large queries; cache by query string (bounded).
const _astCache = new Map()
function parseCached(query) {
  let doc = _astCache.get(query)
  if (!doc) {
    doc = parse(query)
    if (_astCache.size > 200) _astCache.clear()
    _astCache.set(query, doc)
  }
  return doc
}

export function mockResolve(query, variables) {
  const queryStr = typeof query === 'string' ? query : query?.loc?.source?.body || String(query)
  const doc = parseCached(queryStr)
  const fragments = {}
  let operation = null
  for (const def of doc.definitions) {
    if (def.kind === 'FragmentDefinition') fragments[def.name.value] = def
    else if (def.kind === 'OperationDefinition' && !operation) operation = def
  }
  if (!operation) return {}
  const rootType =
    operation.operation === 'mutation'
      ? 'mutation'
      : operation.operation === 'subscription'
        ? 'subscription'
        : 'query'
  // Root seed key is the root type only (not the query text) so that the SAME logical
  // field (e.g. `sites`) is identical no matter which component/selection-set requests it.
  // variables is passed only here so CURATED fixtures apply at the root level only.
  return resolveSelectionSet(
    rootType,
    operation.selectionSet,
    fragments,
    0,
    rootType,
    variables || {}
  )
}
